import React, { useState, useMemo } from "react";
import { Card, ListGroup, Badge, Button, Form, InputGroup } from "react-bootstrap";
import { Droppable, Draggable } from "react-beautiful-dnd";

/* =======================
   TYPES
======================= */
interface OrderItem {
  id: number;
  productName: string;
  quantity: number;
}

interface OrderItemGroup {
  ordersId: number;
  buyerName: string;
  orderNumber?: string;
  targetWarehouse?: string;
  plannedShipmentDate?: string;
  items: OrderItem[];
}

interface OrderItemPoolProps {
  groups: OrderItemGroup[];
}

const getZeroTimeDate = (dateString?: string | Date): Date | null => {
  if (!dateString) return null;
  const d = new Date(dateString);
  d.setHours(0, 0, 0, 0);
  return d;
};

/* =======================
   SUB-COMPONENT: Card
======================= */
const OrderGroupCard = ({ group, index }: { group: OrderItemGroup; index: number }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const displayOrderNumber = group.orderNumber || group.ordersId;

  let dateBadge: React.ReactNode = null;
  if (group.plannedShipmentDate) {
    const targetDate = getZeroTimeDate(group.plannedShipmentDate);
    const today = getZeroTimeDate(new Date());
    if (targetDate && today) {
      const formattedDate = targetDate.toLocaleDateString("tr-TR");
      const diffDays = Math.ceil((targetDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

      let badgeBg = "secondary";
      if (diffDays < 0) badgeBg = "danger";
      else if (diffDays === 0) badgeBg = "success";
      else if (diffDays <= 3) badgeBg = "primary";
      else if (diffDays <= 7) badgeBg = "warning";

      dateBadge = (
        <Badge bg={badgeBg} className="ms-2 fw-normal" style={{ fontSize: "0.65rem" }}>
          {formattedDate}
        </Badge>
      );
    }
  }

  return (
    <Draggable draggableId={`order-${group.ordersId}`} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          // 🔥 GÜNCELLEME: border-secondary kaldırıldı, soft gri (#e3e6f0) eklendi
          className={`mb-3 rounded shadow-sm ${
            snapshot.isDragging ? "bg-primary-subtle border-primary" : "bg-white"
          }`}
          style={{
            ...provided.draggableProps.style,
            border: "1px solid #e3e6f0"
          }}
        >
          <div className="p-3 d-flex justify-content-between align-items-start cursor-pointer" onClick={() => setIsExpanded(!isExpanded)}>
            <div>
              <div className="fw-bold text-primary small mb-1">
                #{displayOrderNumber} {dateBadge}
              </div>
              <div className="text-dark fw-bold small mb-1">{group.buyerName}</div>
              <div className="text-muted small">
                <span style={{ color: "#dc3545", marginRight: "4px" }}>📍</span>
                {group.targetWarehouse || "Depo Yok"}
              </div>
            </div>
            <div className="text-end">
              <Badge bg="light" text="dark" className="border mb-1" style={{borderColor: "#e3e6f0"}}>{group.items.length} Kalem</Badge>
              <div className="text-muted small" style={{ fontSize: "0.7rem" }}>{isExpanded ? "▲ Gizle" : "▼ Detay"}</div>
            </div>
          </div>

          {isExpanded && (
            <div className="border-top bg-light p-2" style={{borderColor: "#e3e6f0"}}>
              <ListGroup variant="flush" className="small">
                {group.items.map((item) => (
                  <ListGroup.Item key={item.id} className="bg-transparent py-1 px-0 d-flex justify-content-between align-items-center border-bottom-0">
                    <span className="text-truncate" style={{ maxWidth: "80%" }}>{item.productName}</span>
                    <span className="fw-bold badge bg-secondary text-white">{item.quantity}</span>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </div>
          )}
        </div>
      )}
    </Draggable>
  );
};

/* =======================
   MAIN COMPONENT
======================= */
const OrderItemPool: React.FC<OrderItemPoolProps> = ({ groups }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFilter, setDateFilter] = useState("ALL");

  const filteredGroups = useMemo(() => {
    return groups.filter((group) => {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = group.buyerName.toLowerCase().includes(searchLower) ||
                            (group.orderNumber && group.orderNumber.toLowerCase().includes(searchLower)) ||
                            (group.targetWarehouse && group.targetWarehouse.toLowerCase().includes(searchLower));
      if (!matchesSearch) return false;
      if (dateFilter === "ALL") return true;
      if (!group.plannedShipmentDate) return false;

      const targetDate = getZeroTimeDate(group.plannedShipmentDate);
      const today = getZeroTimeDate(new Date());
      if (!targetDate || !today) return false;

      const diffDays = Math.ceil((targetDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      if (dateFilter === "PAST") return diffDays < 0;
      if (dateFilter === "TODAY") return diffDays <= 0;
      if (dateFilter === "NEXT_3_DAYS") return diffDays <= 3;
      return true;
    });
  }, [groups, searchTerm, dateFilter]);

  return (
    <div className="col-md-4">
      {/* 🔥 GÜNCELLEME: Dış çerçeve rengi sağ taraftaki board ile eşitlendi (#c5c9d6) */}
      <Card className="h-100 shadow-sm" style={{ backgroundColor: "#f8f9fa", border: "1px solid #c5c9d6" }}>

        <Card.Header className="bg-white p-2" style={{ borderBottom: "1px solid #c5c9d6" }}>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="m-0 text-dark fw-bold">📦 Bekleyenler ({filteredGroups.length})</h6>
            {(searchTerm || dateFilter !== "ALL") && (
              <Button variant="link" size="sm" className="text-danger p-0 text-decoration-none" onClick={() => { setSearchTerm(""); setDateFilter("ALL"); }}>Temizle</Button>
            )}
          </div>

          <InputGroup size="sm" className="mb-2">
            <InputGroup.Text className="bg-light border" style={{borderColor: "#e3e6f0"}}><i className="bi bi-search"></i></InputGroup.Text>
            <Form.Control placeholder="Ara: Müşteri, Depo, No..." className="border" style={{borderColor: "#e3e6f0"}} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
          </InputGroup>

          <Form.Select size="sm" className="border" style={{borderColor: "#e3e6f0"}} value={dateFilter} onChange={(e) => setDateFilter(e.target.value)}>
            <option value="ALL">📅 Tümü</option>
            <option value="PAST">Geçmiş</option>
            <option value="TODAY">Bugün</option>
            <option value="NEXT_3_DAYS">Gelecek 3 Gün</option>
          </Form.Select>
        </Card.Header>

        <Card.Body className="p-2" style={{ overflowY: "auto", maxHeight: "75vh" }}>
          <Droppable droppableId="orderItemPool" type="ORDER">
            {(provided, snapshot) => (
              <div ref={provided.innerRef} {...provided.droppableProps} style={{ minHeight: "100%", backgroundColor: snapshot.isDraggingOver ? "#e9ecef" : "transparent", borderRadius: "8px" }}>
                {filteredGroups.map((group, index) => <OrderGroupCard key={group.ordersId} group={group} index={index} />)}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </Card.Body>
      </Card>
    </div>
  );
};

export default OrderItemPool;